import { Clock } from '@va/icons';
import { useLocale } from '@va/localization';
import { Paragraph } from '@va/ui/design-system';
import { addNumberSeparator, appHasDarkTheme } from '@va/util/helpers';
import React, { useEffect } from 'react';

import { Tooltip, TooltipContent, useTooltipContext } from '@va/ui/tooltips';
import classNames from 'classnames';
import { HIDDEN_DATASET_ID } from './StackedBarChart';

export type StackedBarChartDataset = {
  label: string;
  color: string;
  values: Array<{
    name: string;
    colorClass: string;
    value: string | number;
  }>;
};

type StackBarChartTooltipPropTypes = {
  title?: string;
  currentDatasets: StackedBarChartDataset[];
  currentPeriod: string;
  coordinates: {
    left: number;
    top: number;
  };
  isVisible: boolean;
};

export const StackedBarChartTooltip: React.FC<StackBarChartTooltipPropTypes> = ({
  title,
  currentDatasets,
  currentPeriod,
  coordinates,
  isVisible,
}) => {
  const { locale } = useLocale();
  const isDarkThemeApplied = appHasDarkTheme();

  return (
    <Tooltip open={isVisible}>
      <TooltipTrigger top={coordinates.top} left={coordinates.left} />
      <TooltipContent tooltipClassNames='pointer-events-none'>
        <div className='p-2'>
          {title && (
            <Paragraph
              className='font-semibold mb-1 text-sm sm-initial:text-md'
              colorClassName={classNames({ 'text-white': !isDarkThemeApplied })}
            >
              {title}
            </Paragraph>
          )}
          <Paragraph className='flex gap-2 items-center mb-1 text-xs sm-initial:text-md'>
            <Clock strokeColor='currentColor' />
            <span>{currentPeriod}</span>
          </Paragraph>
          <table>
            <tbody>
              {currentDatasets
                .filter((item) => item.label !== HIDDEN_DATASET_ID)
                .map((item, index) => {
                  return (
                    <tr key={index}>
                      <td className='flex items-center gap-3 '>
                        <div
                          className='rounded-full w-1 h-1 sm-initial:w-2 sm-initial:h-2'
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className='whitespace-nowrap text-xs sm-initial:text-md '>{item.label}</span>
                      </td>
                      {item.values.map((value, index) => {
                        return (
                          <td
                            key={index}
                            className={classNames(
                              'px-1 text-inherit py-1 text-xs whitespace-nowrap sm-initial:text-md sm-initial:px-3 sm-initial:py-3',
                              value.colorClass,
                            )}
                          >
                            {addNumberSeparator(value.value, locale)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const TooltipTrigger = ({ top, left }: { top: number; left: number }) => {
  const { refs, context } = useTooltipContext();

  useEffect(() => {
    context.update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top, left]);

  return (
    <div
      ref={refs.setReference}
      className='absolute'
      style={{
        left: left,
        top: top,
        zIndex: 100,
      }}
    ></div>
  );
};
